// Reading the public catalog: option kinds, prices, badges, facets and the
// filter/sort logic behind the product grid. Pure functions, no React.

import { sortSizes, percentOff } from "./format";

// An option's kind comes from the admin's option styles; listings saved
// before those existed fall back to guessing from the name.
export function optionKind(product, name) {
  const def = (product.optionDefs || []).find((d) => d.name === name);
  if (def?.type) return def.type;
  if (/colou?r|barva/i.test(name)) return "colour";
  return "size";
}

export function optionValues(product, name) {
  const vals = [...new Set(product.variants.map((v) => v.optionValues?.[name]).filter(Boolean))];
  return optionKind(product, name) === "size" ? sortSizes(vals) : vals;
}

export function swatchFor(product, name, value) {
  return (product.optionDefs || []).find((d) => d.name === name)?.swatches?.[value] || null;
}

export function priceInfo(product) {
  const prices = product.variants.map((v) => v.priceMinor);
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;
  const cheapest = product.variants.find((v) => v.priceMinor === min);
  const was = cheapest?.compareAtMinor > min ? cheapest.compareAtMinor : null;
  return { min, max, from: max > min, was, off: percentOff(min, was) };
}

const NEW_DAYS = 21;
export function isNew(product) {
  if (!product.createdAt) return false;
  return Date.now() - new Date(product.createdAt).getTime() < NEW_DAYS * 86400000;
}

// One badge per card, most important first.
export function badgeFor(product) {
  if (product.teased) return { kind: "drop", text: "Coming soon" };
  if (!product.inStock) return { kind: "out", text: product.variants.some((v) => v.reserved) ? "Reserved" : "Sold out" };
  const { off } = priceInfo(product);
  if (off) return { kind: "sale", text: `−${off} %` };
  const left = product.variants.filter((v) => v.inStock);
  if (left.length && left.every((v) => v.lowStock)) return { kind: "low", text: "Last few" };
  if (isNew(product)) return { kind: "new", text: "New" };
  return null;
}

// ---- facets -----------------------------------------------------------------

export function productSizes(p) {
  return p.optionNames.filter((n) => optionKind(p, n) === "size").flatMap((n) => optionValues(p, n));
}

export function productColours(p) {
  return p.optionNames
    .filter((n) => optionKind(p, n) === "colour")
    .flatMap((n) => optionValues(p, n).map((v) => ({ value: v, hex: swatchFor(p, n, v) })));
}

// Sizes/colours count as "available" only when some in-stock variant has them.
function availableValues(p, kind) {
  const names = p.optionNames.filter((n) => optionKind(p, n) === kind);
  return new Set(p.variants.filter((v) => v.inStock).flatMap((v) => names.map((n) => v.optionValues?.[n]).filter(Boolean)));
}

// Price filter: whole CZK, either end open ("300-", "-900", "300-900").
export function parsePrice(raw) {
  const m = String(raw || "").match(/^(\d*)-(\d*)$/);
  if (!m) return { min: null, max: null };
  const min = m[1] === "" ? null : Number(m[1]);
  const max = m[2] === "" ? null : Number(m[2]);
  return min != null && max != null && min > max ? { min: max, max: min } : { min, max };
}
export const hasPrice = (price) => price.min != null || price.max != null;
export function priceLabel(price) {
  const k = (n) => `${n.toLocaleString("cs-CZ")} Kč`;
  if (price.min != null && price.max != null) return `${price.min.toLocaleString("cs-CZ")} – ${k(price.max)}`;
  return price.min != null ? `From ${k(price.min)}` : `Up to ${k(price.max)}`;
}

export const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price, low to high" },
  { id: "price-desc", label: "Price, high to low" },
];

// The filter state lives in the URL (shareable, survives back/forward).
export function readFilters(params) {
  const list = (k) => (params.get(k) || "").split(",").map((s) => s.trim()).filter(Boolean);
  return {
    q: params.get("q") || "",
    category: params.get("c") || "",
    sizes: list("size"),
    colours: list("colour"),
    price: parsePrice(params.get("price")),
    inStock: params.get("stock") === "1",
    sale: params.get("sale") === "1",
    sort: SORTS.some((s) => s.id === params.get("sort")) ? params.get("sort") : "featured",
  };
}

export function writeFilters(f) {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.category) p.set("c", f.category);
  if (f.sizes.length) p.set("size", f.sizes.join(","));
  if (f.colours.length) p.set("colour", f.colours.join(","));
  if (hasPrice(f.price)) p.set("price", `${f.price.min ?? ""}-${f.price.max ?? ""}`);
  if (f.inStock) p.set("stock", "1");
  if (f.sale) p.set("sale", "1");
  if (f.sort !== "featured") p.set("sort", f.sort);
  return p;
}

export const activeFilterCount = (f) => f.sizes.length + f.colours.length + (hasPrice(f.price) ? 1 : 0) + (f.inStock ? 1 : 0) + (f.sale ? 1 : 0);

const norm = (s) =>
  String(s || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export function matchesQuery(p, q) {
  const words = norm(q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = norm(
    [p.title, p.description, p.story, p.category?.name, p.collection?.name, ...(p.tags || []), ...productSizes(p), ...productColours(p).map((c) => c.value)].join(" ")
  );
  return words.every((w) => hay.includes(w));
}

// Everything except one facet, so each facet can show what picking it would give.
function passes(p, f, skip) {
  if (skip !== "q" && !matchesQuery(p, f.q)) return false;
  if (skip !== "category" && f.category && p.category?.slug !== f.category) return false;
  if (skip !== "sizes" && f.sizes.length) {
    const avail = availableValues(p, "size");
    if (!f.sizes.some((s) => avail.has(s))) return false;
  }
  if (skip !== "colours" && f.colours.length) {
    const avail = availableValues(p, "colour");
    if (!f.colours.some((c) => avail.has(c))) return false;
  }
  if (skip !== "price" && hasPrice(f.price)) {
    const czk = priceInfo(p).min / 100;
    if (f.price.min != null && czk < f.price.min) return false;
    if (f.price.max != null && czk > f.price.max) return false;
  }
  if (skip !== "inStock" && f.inStock && !p.inStock) return false;
  if (skip !== "sale" && f.sale && !priceInfo(p).off) return false;
  return true;
}

export function applyFilters(products, f) {
  const list = products.filter((p) => passes(p, f));
  const by = {
    newest: (a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
    "price-asc": (a, b) => priceInfo(a).min - priceInfo(b).min,
    "price-desc": (a, b) => priceInfo(b).min - priceInfo(a).min,
  }[f.sort];
  // Featured keeps the admin's order, but sold-out pieces sink to the end.
  const sorted = by ? [...list].sort(by) : [...list].sort((a, b) => Number(!a.inStock && !a.teased) - Number(!b.inStock && !b.teased));
  return sorted;
}

// Facet options with live counts under the other active filters.
export function facets(products, f) {
  const count = (skip, test) => products.filter((p) => passes(p, f, skip) && test(p)).length;
  const sizes = sortSizes([...new Set(products.flatMap(productSizes))]).map((value) => ({
    value,
    count: count("sizes", (p) => availableValues(p, "size").has(value)),
  }));
  const colourMap = new Map();
  for (const p of products) for (const c of productColours(p)) if (!colourMap.has(c.value) || (!colourMap.get(c.value) && c.hex)) colourMap.set(c.value, c.hex);
  const colours = [...colourMap].map(([value, hex]) => ({ value, hex, count: count("colours", (p) => availableValues(p, "colour").has(value)) }));
  const price = priceScale(products, (p) => passes(p, f, "price"));
  const categories = [...new Map(products.filter((p) => p.category).map((p) => [p.category.slug, p.category])).values()].map((c) => ({
    ...c,
    count: count("category", (p) => p.category?.slug === c.slug),
  }));
  return {
    sizes,
    colours,
    price,
    categories,
    inStock: count("inStock", (p) => p.inStock),
    sale: count("sale", (p) => priceInfo(p).off > 0),
  };
}

// The price slider's scale: whole CZK over every piece in scope (so it doesn't
// shift as other filters change), and a histogram of the pieces that pass the
// other filters. null when everything costs the same (nothing to slide).
const BUCKETS = 12;
function priceScale(products, passing) {
  const all = products.map((p) => priceInfo(p).min / 100);
  if (!all.length) return null;
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const step = hi - lo > 2000 ? 50 : 10;
  const min = Math.floor(lo / step) * step;
  const max = Math.max(Math.ceil(hi / step) * step, min + step);
  if (hi === lo) return null;
  const width = (max - min) / BUCKETS;
  const bars = Array.from({ length: BUCKETS }, (_, i) => ({ from: min + i * width, to: min + (i + 1) * width, count: 0 }));
  for (const p of products) {
    if (!passing(p)) continue;
    const czk = priceInfo(p).min / 100;
    bars[Math.min(BUCKETS - 1, Math.floor((czk - min) / width))].count += 1;
  }
  return { min, max, step, bars };
}
